// SideMenu.tsx (or the file containing UserSection)
import * as React from "react";
import { useState, useEffect } from "react"; // Import useState and useEffect
import { styled } from "@mui/material/styles";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MenuContent from "./MenuContent";
import logo from "../assets/logo.svg";
import { client } from "../adal"; // Only import client, will manage account directly
import Grid from "@mui/material/Grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Logout from "@mui/icons-material/Logout";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { IconButton, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';

interface SideMenuProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  drawerWidth: number;
  closedDrawerWidth: number;
}

const CustomDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth' && prop !== 'closedDrawerWidth',
})<{ open: boolean; drawerWidth: number; closedDrawerWidth: number }>(
  ({ theme, open, drawerWidth, closedDrawerWidth }) => ({
    width: open ? drawerWidth : closedDrawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [`& .${drawerClasses.paper}`]: {
      width: open ? drawerWidth : closedDrawerWidth,
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
);

export default function SideMenu({ open, setOpen, drawerWidth, closedDrawerWidth }: SideMenuProps) {
  // Manage the active user state directly within SideMenu
  const [currentUser, setCurrentUser] = useState(client.account());

  useEffect(() => {
    // This effect runs once when the component mounts.
    // We need to subscribe to MSAL events to update our state.

    const updateAccountState = () => {
      // client.account() is a getter, so we call it to get the latest.
      // We also check all accounts and set the active one for good measure.
      const accounts = client.msalInstance.getAllAccounts();
      if (
        accounts.length > 0 &&
        client.msalInstance.getActiveAccount() === null
      ) {
        // If there are accounts but no active one, set the first one as active
        client.msalInstance.setActiveAccount(accounts[0]);
      }
      setCurrentUser(client.account());
    };

    // Initial check
    updateAccountState();

    // Subscribe to MSAL events for account changes
    // PublicClientApplication.addEventCallback is the way to do this.
    // EventType.ACCOUNT_ADDED, EventType.ACCOUNT_REMOVED, EventType.LOGIN_SUCCESS, EventType.LOGOUT_SUCCESS
    const callbackId = client.msalInstance.addEventCallback((event) => {
      if (
        event.eventType === "msal:loginSuccess" ||
        event.eventType === "msal:accountAdded" ||
        event.eventType === "msal:accountRemoved" ||
        event.eventType === "msal:logoutSuccess" // Logout success might mean currentUser becomes null
      ) {
        console.log(`MSAL Event received: ${event.eventType}`, event);
        updateAccountState();
      }
    });

    // Cleanup: Remove the event callback when the component unmounts
    return () => {
      if (callbackId) {
        client.msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return (
      <CustomDrawer
        variant="permanent"
        open={open}
        drawerWidth={drawerWidth}
        closedDrawerWidth={closedDrawerWidth}>
      <Box
        sx={{
          display: "flex",
          mt: "calc(var(--template-frame-height, 0px) + 4px)",
          p: 1.5,
        }}
      >
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="flex-start"
        >
          <Grid>
            <img
              src={logo}
              alt="CannonDesign Logo"
              style={{ height: open ? 40 : 25, marginRight: open ? 16 : 0, transition: 'height 0.3s, margin 0.3s' }}
            />
          </Grid>
          <Grid>
            {open && <span style={{ fontSize: 18, fontWeight: 500 }}>CannonDesign</span>}
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List dense sx={{ paddingBottom: '0 !important'}}>
          <ListItem disablePadding sx={{ display: 'block' }} >
            <ListItemButton
                  sx={{
                  height: '36px',
                  width: '33px',
                  mr: open ? 2 : 0,
                  ml: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              onClick={() => setOpen(!open)}>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  height: '36px',
                  mr: open ? 2 : 0,
                  pl: open ? 2 : 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {open ? <ChevronLeftIcon /> : <MenuIcon />}
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
        <MenuContent />
      </Box>
      {/* Pass currentUser as a prop */}
      {currentUser && (
        <UserSectionInner user={currentUser} setCurrentUser={setCurrentUser} />
      )}
      {/* Optionally handle case where no user is logged in */}
      {!currentUser && (
        <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
          Not logged in.
          {/* You could add a login button here if desired */}
        </Box>
      )}
    </CustomDrawer>
  );
}

// Renamed UserSection to UserSectionInner to avoid name conflict if both were in same file
// This component now receives 'user' and 'setCurrentUser' from its parent
interface UserSectionInnerProps {
  user: any; // AccountInfo
  setCurrentUser: React.Dispatch<React.SetStateAction<any | null>>; // Function to update user state
}

const UserSectionInner: React.FC<UserSectionInnerProps> = ({
  user,
  setCurrentUser,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUseAnotherAccount = async () => {
    handleClose();

    try {
      // Option 1: Directly use loginPopup.
      // This will set the new account as active in MSAL and trigger MSAL events.
      const result = await client.msalInstance.loginPopup({
        scopes: ["openid", "profile", "email", "offline_access"],
        prompt: "select_account",
      });

      if (result && result.account) {
        console.log(
          "Successfully signed in with another account via popup:",
          result.account.username.toLocaleLowerCase()
        );
        setCurrentUser(result.account);
        setUserName(result.account.username.toLocaleLowerCase());

        // The MSAL event callback in SideMenu will catch this and update currentUser.
        // No explicit window.location.reload() or setCurrentUser here is needed
        // if your event callback handles it.
        // If you still want to force a refresh, window.location.reload() is a fallback.
      } else {
        console.warn(
          "Popup login for another account did not return an account."
        );
      }
    } catch (error) {
      console.error("Error during 'Use Another Account' via popup:", error);
    }
  };

  const handleLogout = async () => {
    handleClose();
    try {
      client.logout();
      // The MSAL event callback in SideMenu will catch 'msal:logoutSuccess' and update currentUser to null.
    } catch (error) {
      console.error("Error during 'Logout':", error);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          p: 2,
          gap: 1,
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <AccountCircle  />
        <Box sx={{ mr: "auto" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, lineHeight: "16px", ml: open ? 0 : 2 }}
          >
            {user?.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {userName}
          </Typography>
        </Box>
      </Stack>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box sx={{ p: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, lineHeight: "16px" }}
          >
            {user?.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {userName}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleUseAnotherAccount}>
          <ListItemIcon>
            <PersonAdd fontSize="small" />
          </ListItemIcon>
          Use Another Account
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};
